import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../App';
import toast from 'react-hot-toast';

export default function Referrals() {
  const { user } = useAuth();
  const [referral, setReferral] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bonusPerRef, setBonusPerRef] = useState(50);
  const [bonusForReferred, setBonusForReferred] = useState(25);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/api/referral/me'),
      api.get('/api/referral/leaderboard'),
    ]).then(([me, lb]) => {
      setReferral(me.data.referral);
      setBonusPerRef(me.data.bonus_per_referral);
      setBonusForReferred(me.data.bonus_for_referred);
      setLeaderboard(lb.data.leaderboard || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const copyCode = () => {
    if (!referral) return;
    navigator.clipboard.writeText(referral.code);
    setCopied(true);
    toast.success('Referral code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLink = () => {
    if (!referral) return;
    const link = `${window.location.origin}/auth?ref=${referral.code}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="referrals-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-text-primary tracking-tight flex items-center gap-3">
          <i className="fa-solid fa-gift text-primary" />Referral Program
        </h1>
        <p className="text-text-muted text-sm mt-1">Invite solar pros, earn credits for every signup</p>
      </div>

      {/* Hero Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary to-emerald-600 p-8 text-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-2xl font-bold mb-2">Earn {bonusPerRef} Credits Per Referral</h2>
            <p className="text-sm opacity-90 max-w-md">Share your code with fellow solar professionals. When they sign up, you both earn bonus credits — it's a win-win.</p>
            <div className="flex items-center gap-4 mt-4">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2">
                <i className="fa-solid fa-coins text-yellow-300" />
                <span className="text-sm font-medium">You get <b>{bonusPerRef}</b> credits</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2">
                <i className="fa-solid fa-user-plus text-green-300" />
                <span className="text-sm font-medium">They get <b>{bonusForReferred}</b> credits</span>
              </div>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 min-w-[200px] text-center">
            <p className="text-xs opacity-70 uppercase tracking-widest mb-2">Your Code</p>
            <p className="text-3xl font-heading font-bold tracking-wider" data-testid="referral-code">{referral?.code || '---'}</p>
            <div className="flex gap-2 mt-3">
              <button onClick={copyCode} className="flex-1 bg-white text-primary py-2 rounded-lg text-xs font-semibold hover:bg-green-50 transition-all" data-testid="copy-code-btn">
                <i className={`fa-solid ${copied ? 'fa-check' : 'fa-copy'} mr-1`} />{copied ? 'Copied!' : 'Copy Code'}
              </button>
              <button onClick={copyLink} className="flex-1 bg-white/20 py-2 rounded-lg text-xs font-semibold hover:bg-white/30 transition-all" data-testid="copy-link-btn">
                <i className="fa-solid fa-link mr-1" />Copy Link
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { l: 'Total Referrals', v: referral?.total_referrals || 0, icon: 'fa-users', color: 'primary' },
          { l: 'Credits Earned', v: referral?.total_credits_earned || 0, icon: 'fa-coins', color: 'accent' },
          { l: 'Your Credits', v: user?.credits || 0, icon: 'fa-wallet', color: 'secondary' },
          { l: 'Leaderboard Rank', v: `#${(leaderboard.findIndex(l => l.user_email === user?.email) + 1) || '-'}`, icon: 'fa-ranking-star', color: 'primary' },
        ].map((s, i) => (
          <div key={s.l} className="card-interactive p-4 animate-slide-up" style={{ animationDelay: `${i * 60}ms` }}>
            <div className="flex items-center gap-2 mb-2">
              <i className={`fa-solid ${s.icon} text-${s.color} text-xs`} />
              <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">{s.l}</span>
            </div>
            <p className={`font-heading text-2xl font-bold text-${s.color === 'primary' ? 'text-primary' : s.color} tabular-nums`}>{s.v}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Referral History */}
        <div className="card-interactive overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-surface-alt">
            <h3 className="text-sm font-semibold text-text-primary">Your Referrals</h3>
          </div>
          {(!referral?.referrals || referral.referrals.length === 0) ? (
            <div className="py-12 text-center">
              <i className="fa-solid fa-user-group text-3xl text-text-muted opacity-20 mb-3 block" />
              <p className="text-text-muted text-sm font-medium">No referrals yet</p>
              <p className="text-text-muted text-xs mt-1">Share your code to start earning!</p>
            </div>
          ) : (
            <div className="divide-y divide-border/50">
              {referral.referrals.map((r, i) => (
                <div key={i} className="px-4 py-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold">{r.name?.charAt(0)}</div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-text-primary">{r.name}</p>
                    <p className="text-[11px] text-text-muted">{r.email}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold text-primary">+{bonusPerRef} credits</span>
                    <p className="text-[10px] text-text-muted">{new Date(r.date).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Referrers */}
        <div className="card-interactive overflow-hidden" data-testid="referral-leaderboard">
          <div className="px-4 py-3 border-b border-border bg-surface-alt">
            <h3 className="text-sm font-semibold text-text-primary">Top Referrers</h3>
          </div>
          {leaderboard.length === 0 ? (
            <div className="py-12 text-center">
              <i className="fa-solid fa-ranking-star text-3xl text-text-muted opacity-20 mb-3 block" />
              <p className="text-text-muted text-sm">Be the first referrer!</p>
            </div>
          ) : (
            <div className="divide-y divide-border/50">
              {leaderboard.slice(0, 10).map((entry, i) => {
                const isMe = entry.user_email === user?.email;
                return (
                  <div key={i} className={`px-4 py-3 flex items-center gap-3 ${isMe ? 'bg-primary/5' : ''}`}>
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${i < 3 ? 'bg-primary text-white' : 'bg-surface-alt text-text-muted'}`}>{i + 1}</span>
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-primary text-xs font-bold">{entry.user_name?.charAt(0)}</div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-text-primary">{entry.user_name} {isMe && <span className="text-xs text-primary">(You)</span>}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-primary tabular-nums">{entry.total_referrals}</p>
                      <p className="text-[10px] text-text-muted">referrals</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* How It Works */}
      <div className="card-interactive p-6">
        <h3 className="font-heading text-lg font-bold text-text-primary mb-4 text-center">How It Works</h3>
        <div className="grid grid-cols-4 gap-6 max-w-3xl mx-auto">
          {[
            { step: 1, title: 'Share Code', desc: 'Send your unique referral code to other solar professionals', icon: 'fa-share-nodes' },
            { step: 2, title: 'They Register', desc: 'New users enter your code during sign-up', icon: 'fa-user-plus' },
            { step: 3, title: 'Both Earn', desc: `You get ${bonusPerRef} credits, they get ${bonusForReferred} credits`, icon: 'fa-coins' },
            { step: 4, title: 'Repeat', desc: 'No limit! Keep referring and earning', icon: 'fa-repeat' },
          ].map(s => (
            <div key={s.step} className="text-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <i className={`fa-solid ${s.icon} text-primary`} />
              </div>
              <p className="text-sm font-semibold text-text-primary">{s.title}</p>
              <p className="text-[10px] text-text-muted mt-1">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
